import { Link } from 'react-router-dom';
import { Shield, CheckCircle, Dog, MapPin, Search, Siren, HardHat, Clock, FileCheck, Award } from 'lucide-react';

const k9Handler = "https://customer-assets.emergentagent.com/job_surfside-guard/artifacts/jp5zo9ku_IMG_1273.jpeg";
const k9Dog = "https://customer-assets.emergentagent.com/job_surfside-guard/artifacts/0h56cfcn_IMG_1272.jpeg";

const k9Services = [
  {
    icon: <Dog className="h-6 w-6" />,
    title: 'Patrol & Detection K9 Units',
    description: 'Our security canines are professionally trained in area patrol, perimeter defense, and human detection. Each dog operates alongside a certified handler to cover large properties efficiently — providing a level of sensory detection that technology alone cannot match.',
    ideal: 'Ideal for warehouses, construction sites, and industrial yards.'
  },
  {
    icon: <Search className="h-6 w-6" />,
    title: 'Intruder Detection & Tracking',
    description: 'K9 units excel at locating concealed individuals on your property. Our dogs can detect human scent through walls, vehicles, dense vegetation, and enclosed spaces. When a breach is detected, the K9 team responds immediately to track, locate, and contain the intruder until law enforcement arrives.',
    ideal: 'Critical for after-hours property security and breach response.'
  },
  {
    icon: <MapPin className="h-6 w-6" />,
    title: 'Perimeter Patrol Coverage',
    description: 'Scheduled and randomized K9 patrols along fence lines, loading docks, access points, and blind spots. The visible presence of a trained security dog is one of the most effective deterrents available — studies show K9 patrols reduce property crime by up to 70% compared to static guard posts.',
    ideal: 'Perfect for large properties, storage facilities, and open lots.'
  },
  {
    icon: <Siren className="h-6 w-6" />,
    title: 'Event Security K9 Support',
    description: 'Trained K9 units provide crowd control support, perimeter security, and VIP protection at events. Our handlers manage the dogs in high-traffic environments with precision — maintaining public safety while projecting authority and deterrence.',
    ideal: 'Ideal for concerts, sporting events, corporate functions, and festivals.'
  },
  {
    icon: <HardHat className="h-6 w-6" />,
    title: 'Construction & Industrial Sites',
    description: 'Construction theft and copper wire theft cost the industry billions annually. Our K9 teams provide overnight and weekend patrols of active job sites, detecting unauthorized entry and deterring material theft. Dogs can cover a 10-acre site in a fraction of the time a foot patrol officer would require.',
    ideal: 'Essential for active construction zones and equipment yards.'
  },
  {
    icon: <Clock className="h-6 w-6" />,
    title: 'Rapid Response Deployment',
    description: 'Our K9 units are available for on-demand emergency deployment. When a security breach, trespasser, or suspicious activity is reported, a K9 team can be dispatched to your location to conduct a thorough sweep and secure the premises. Available 24/7 through our dispatch center.',
    ideal: 'Available around the clock for emergency callouts.'
  }
];

const k9Qualifications = [
  { icon: <Award className="h-5 w-5" />, text: 'Professionally Trained Security Canines' },
  { icon: <Shield className="h-5 w-5" />, text: 'Certified & Licensed K9 Handlers' },
  { icon: <FileCheck className="h-5 w-5" />, text: 'Ongoing Training & Recertification' },
  { icon: <CheckCircle className="h-5 w-5" />, text: 'Fully Insured K9 Operations' },
  { icon: <Dog className="h-5 w-5" />, text: 'Breed-Specific Selection for Security Work' },
  { icon: <Clock className="h-5 w-5" />, text: '24/7 Dispatch Availability' }
];

export const K9Section = () => {
  return (
    <div id="k9-security" className="py-20 bg-[#121212]" data-testid="service-section-k9-security">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          <div className="lg:order-2">
            <p className="text-[#F5C518] text-sm font-mono uppercase tracking-widest mb-2">Canine Security Units</p>
            <h2 className="text-3xl md:text-4xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
              K9 Property Guarding.<br /><span className="text-[#F5C518]">Superior Deterrence.</span>
            </h2>
            <p className="text-zinc-400 leading-relaxed mb-6">
              Our K9 security division pairs highly trained security dogs with experienced, certified handlers to deliver property protection that no technology or human patrol alone can replicate. The combination of acute canine senses, physical deterrence, and handler expertise makes our K9 units the most effective perimeter defense available.
            </p>
            <div className="bg-[#0A0A0A] border-l-4 border-[#F5C518] p-4 mb-6">
              <p className="text-white font-semibold">Not a pet with a vest.</p>
              <p className="text-[#F5C518]">A trained, certified security asset.</p>
            </div>
            <Link to="/contact" className="btn-skew inline-block bg-[#F5C518] text-black font-bold uppercase tracking-wider px-6 py-3 hover:bg-[#E5B508] transition-all duration-300" data-testid="service-cta-k9-security">
              <span>Request K9 Services</span>
            </Link>
          </div>
          <div className="lg:order-1 grid grid-cols-2 gap-4">
            <img src={k9Handler} alt="K9 Handler" className="w-full aspect-[3/4] object-cover border border-zinc-800" />
            <img src={k9Dog} alt="Security K9" className="w-full aspect-[3/4] object-cover border border-zinc-800" />
          </div>
        </div>

        {/* Service Capabilities */}
        <div className="mb-20">
          <h3 className="text-2xl font-bold uppercase tracking-tight text-white mb-8 font-['Barlow_Condensed'] text-center">
            K9 <span className="text-[#F5C518]">Capabilities</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {k9Services.map((service) => (
              <div key={service.title} className="bg-[#0A0A0A] border border-zinc-800 p-6 hover:border-[#F5C518]/50 transition-colors">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-[#F5C518]/10 text-[#F5C518] mb-4">
                  {service.icon}
                </div>
                <h4 className="text-lg font-bold text-white mb-3 font-['Barlow_Condensed'] uppercase">{service.title}</h4>
                <p className="text-zinc-400 text-sm mb-3">{service.description}</p>
                <p className="text-[#F5C518] text-xs font-semibold">{service.ideal}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Qualifications & Deployment */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
          <div className="bg-[#0A0A0A] border border-zinc-800 p-8">
            <h3 className="text-xl font-bold uppercase tracking-tight text-white mb-6 font-['Barlow_Condensed']">
              Handler <span className="text-[#F5C518]">Qualifications</span>
            </h3>
            <div className="space-y-4">
              {k9Qualifications.map((item) => (
                <div key={item.text} className="flex items-center text-zinc-400">
                  <span className="text-[#F5C518] mr-3">{item.icon}</span>
                  {item.text}
                </div>
              ))}
            </div>
            <p className="text-zinc-500 text-sm mt-6 pt-6 border-t border-zinc-800">
              Every handler and canine team undergoes continuous evaluation and recertification to ensure peak readiness.
            </p>
          </div>

          <div className="bg-[#0A0A0A] border border-zinc-800 p-8">
            <h3 className="text-xl font-bold uppercase tracking-tight text-white mb-6 font-['Barlow_Condensed']">
              Deployment <span className="text-[#F5C518]">Options</span>
            </h3>
            <p className="text-zinc-400 mb-4">Flexible K9 security packages tailored to your needs:</p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start text-zinc-400 text-sm">
                <CheckCircle className="h-4 w-4 text-[#F5C518] mr-2 mt-0.5 flex-shrink-0" />
                Nightly patrols with scheduled sweeps
              </li>
              <li className="flex items-start text-zinc-400 text-sm">
                <CheckCircle className="h-4 w-4 text-[#F5C518] mr-2 mt-0.5 flex-shrink-0" />
                Weekend and holiday coverage
              </li>
              <li className="flex items-start text-zinc-400 text-sm">
                <CheckCircle className="h-4 w-4 text-[#F5C518] mr-2 mt-0.5 flex-shrink-0" />
                Event-specific K9 details
              </li>
              <li className="flex items-start text-zinc-400 text-sm">
                <CheckCircle className="h-4 w-4 text-[#F5C518] mr-2 mt-0.5 flex-shrink-0" />
                On-demand emergency deployment
              </li>
              <li className="flex items-start text-zinc-400 text-sm">
                <CheckCircle className="h-4 w-4 text-[#F5C518] mr-2 mt-0.5 flex-shrink-0" />
                Long-term contract patrols with dedicated K9 teams
              </li>
              <li className="flex items-start text-zinc-400 text-sm">
                <CheckCircle className="h-4 w-4 text-[#F5C518] mr-2 mt-0.5 flex-shrink-0" />
                Combined K9 + armed guard packages
              </li>
            </ul>
            <p className="text-[#F5C518] text-sm font-semibold">
              Custom packages available. Contact us for a site assessment.
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-[#0A0A0A] border border-[#F5C518]/30 p-8 text-center">
          <h3 className="text-2xl font-bold uppercase tracking-tight text-white mb-4 font-['Barlow_Condensed']">
            Add K9 Power to Your <span className="text-[#F5C518]">Security Plan</span>
          </h3>
          <p className="text-zinc-400 mb-6 max-w-2xl mx-auto">
            Contact us to schedule a free property assessment and learn how K9 security can dramatically improve your protection coverage.
          </p>
          <Link to="/contact" className="btn-skew inline-block bg-[#F5C518] text-black font-bold uppercase tracking-wider px-8 py-4 hover:bg-[#E5B508] transition-all duration-300">
            <span>Get K9 Security Quote</span>
          </Link>
        </div>
      </div>
    </div>
  );
};
